import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Github, Twitter, Linkedin, Youtube } from 'lucide-react';

/**
 * Footer — global, shown on every non-print page.
 *
 * Four columns:
 *   1. Product     — what users can do here
 *   2. Learn       — educational surfaces
 *   3. Company     — marketing / about
 *   4. Legal       — terms, privacy, contact
 *
 * Plus: brand strip with tagline, social icons, copyright line.
 * Routes that don't exist yet (e.g. /about, /blog) are wired to "#" placeholders
 * and will be upgraded as those pages ship. They still render as anchor tags so
 * screen readers + styling are consistent.
 */

const Discord = (props) => (
  // lucide-react has no official Discord glyph, so we inline the official SVG
  // mark (MIT-style usage) to keep icon parity with the other socials.
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    width="20"
    height="20"
    aria-hidden="true"
    {...props}
  >
    <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.08.08 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.699.772 1.364 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

const LINK_COLUMNS = [
  {
    heading: 'Product',
    testid: 'footer-col-product',
    items: [
      { label: 'Rooms', to: '/rooms' },
      { label: 'Learning Paths', to: '/paths' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Dashboard', to: '/dashboard' },
    ],
  },
  {
    heading: 'Learn',
    testid: 'footer-col-learn',
    items: [
      { label: 'Leaderboard', to: '/leaderboard' },
      { label: 'Daily Challenge', to: '/dashboard' },
      { label: 'Certificates', to: '/profile' },
      { label: 'CTF Writeups', to: '#' },
    ],
  },
  {
    heading: 'Company',
    testid: 'footer-col-company',
    items: [
      { label: 'About', to: '#' },
      { label: 'Blog', to: '#' },
      { label: 'Careers', to: '#' },
      { label: 'Press Kit', to: '#' },
    ],
  },
  {
    heading: 'Legal',
    testid: 'footer-col-legal',
    items: [
      { label: 'Terms of Service', to: '#' },
      { label: 'Privacy Policy', to: '#' },
      { label: 'Security', to: '#' },
      { label: 'Contact', to: 'mailto:hello@cyberinsti.com' },
    ],
  },
];

const SOCIALS = [
  { label: 'GitHub', href: 'https://github.com/', Icon: Github, testid: 'footer-social-github' },
  { label: 'X (Twitter)', href: 'https://twitter.com/', Icon: Twitter, testid: 'footer-social-twitter' },
  { label: 'LinkedIn', href: 'https://linkedin.com/', Icon: Linkedin, testid: 'footer-social-linkedin' },
  { label: 'YouTube', href: 'https://youtube.com/', Icon: Youtube, testid: 'footer-social-youtube' },
  { label: 'Discord', href: 'https://discord.com/', Icon: Discord, testid: 'footer-social-discord' },
];

const YEAR = new Date().getFullYear();

// Anchor vs Router Link: use <a> for mailto, "#", and external URLs;
// <Link> for in-app routes so navigation stays client-side.
const SmartLink = ({ to, children, ...rest }) => {
  const isExternal =
    to === '#' ||
    to.startsWith('mailto:') ||
    to.startsWith('http://') ||
    to.startsWith('https://');
  if (isExternal) {
    return (
      <a
        href={to}
        target={to.startsWith('http') ? '_blank' : undefined}
        rel={to.startsWith('http') ? 'noopener noreferrer' : undefined}
        {...rest}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={to} {...rest}>
      {children}
    </Link>
  );
};

export const Footer = () => {
  return (
    <footer
      data-testid="global-footer"
      className="border-t border-gray-800 bg-[#070707] text-gray-400 mt-16 print:hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Top grid: brand + 4 link columns */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          {/* Brand block — spans 2 cols on md+ */}
          <div className="col-span-2">
            <div className="flex items-center space-x-2 mb-3">
              <Shield className="w-7 h-7 text-[#00FF00]" />
              <span className="text-lg font-bold text-white">CyberInsti</span>
            </div>
            <p className="text-sm leading-relaxed max-w-xs">
              Train like an attacker. Defend like a pro. Hands-on cybersecurity
              labs, CTF challenges, and AI-powered threat detection.
            </p>
            <div
              className="flex items-center gap-3 mt-5"
              data-testid="footer-socials"
            >
              {SOCIALS.map(({ label, href, Icon, testid }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  data-testid={testid}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-800 text-gray-400 hover:text-[#00FF00] hover:border-[#00FF00]/50 hover:bg-[#00FF00]/5 transition-colors"
                >
                  <Icon className="w-[18px] h-[18px]" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {LINK_COLUMNS.map((col) => (
            <div key={col.heading} data-testid={col.testid}>
              <h4 className="text-white font-semibold text-sm mb-3 tracking-wide">
                {col.heading}
              </h4>
              <ul className="space-y-2">
                {col.items.map((item) => (
                  <li key={item.label}>
                    <SmartLink
                      to={item.to}
                      className="text-sm text-gray-400 hover:text-[#00FF00] transition-colors"
                    >
                      {item.label}
                    </SmartLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom strip: copyright + tiny status hint */}
        <div className="mt-10 pt-6 border-t border-gray-900 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p
            className="text-xs text-gray-500"
            data-testid="footer-copyright"
          >
            © {YEAR} CyberInsti Labs. All rights reserved.
          </p>
          <p className="text-xs text-gray-600">
            Built for the next generation of cybersecurity defenders.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
